import { NgOptimizedImage } from '@angular/common';
import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { SPACE_CARD_IMAGE_SIZES } from '../../shared/storage/space-image.constants';
import { Router, RouterLink } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { SiteFooterComponent } from '../site-footer/site-footer.component';
import { AssetService } from '../../services/asset.service';
import { minHourlyRate, spaceCoverThumb, Space, SpaceCategory, SPACE_CATEGORY_LABELS, getSpaceCategories } from '../../models/asset.model';
import { spaceLocationLabel as resolveSpaceLocationLabel } from '../../shared/location/address-resolution.utils';
import { spacePublicPathCommands } from '../../shared/location/space-public-url.utils';
import { SeoService } from '../../services/seo.service';
import { SEO_DEFAULTS } from '../../shared/seo/seo.utils';

export interface SpaceCategoryCard {
  label: string;
  sublabel: string;
  placeholderBg: string;
  filter: SpaceCategory;
}

@Component({
  selector: 'app-landing-user',
  imports: [RouterLink, NgOptimizedImage, SiteFooterComponent, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './landing-user.component.html',
})
export class LandingUserComponent implements OnInit, OnDestroy {
  readonly cardImageSizes = SPACE_CARD_IMAGE_SIZES;

  private router       = inject(Router);
  private assetService = inject(AssetService);
  private seo          = inject(SeoService);

  readonly latestSpaces  = signal<Space[]>([]);
  readonly spacesLoading = signal(true);

  readonly categories = signal<SpaceCategoryCard[]>([
    {
      label:         'Celebraciones y Fiestas',
      sublabel:      'Locales privados para eventos sociales y celebraciones únicas.',
      placeholderBg: 'linear-gradient(160deg, #0f1117 0%, #1e2433 100%)',
      filter:        'sala_fiestas',
    },
    {
      label:         'Estudios y Set Creativo',
      sublabel:      'Estudios fotográficos, de grabación y producción de contenido.',
      placeholderBg: 'linear-gradient(160deg, #111318 0%, #1c2030 100%)',
      filter:        'estudio',
    },
    {
      label:         'Reuniones y Workshops',
      sublabel:      'Salas de reunión, talleres y espacios de trabajo profesional.',
      placeholderBg: 'linear-gradient(160deg, #0e1016 0%, #1a1f2e 100%)',
      filter:        'coworking',
    },
    {
      label:         'Terrazas y Áticos',
      sublabel:      'Azoteas, terrazas y patios urbanos para eventos al aire libre.',
      placeholderBg: 'linear-gradient(160deg, #10100e 0%, #1e1c18 100%)',
      filter:        'terraza',
    },
    {
      label:         'Cocinas y Showcooking',
      sublabel:      'Cocinas equipadas para clases, showcookings y eventos gastronómicos.',
      placeholderBg: 'linear-gradient(160deg, #0f1008 0%, #1c1e10 100%)',
      filter:        'cocina',
    },
    {
      label:         'Espacios Singulares',
      sublabel:      'Galerías, naves industriales y espacios únicos para proyectos especiales.',
      placeholderBg: 'linear-gradient(160deg, #100f18 0%, #1d1b2a 100%)',
      filter:        'singular',
    },
  ]);

  ngOnInit(): void {
    this.seo.setPage({
      title: `Alquila espacios por horas | ${SEO_DEFAULTS.siteName}`,
      description: SEO_DEFAULTS.defaultDescription,
      canonicalPath: '/',
    });

    this.assetService.getLatestSpaces(9).then(spaces => {
      this.latestSpaces.set(spaces);
      this.spacesLoading.set(false);
    }).catch(() => this.spacesLoading.set(false));
  }

  ngOnDestroy(): void {
    this.seo.resetToDefault();
  }

  exploreCategory(filter: SpaceCategory): void {
    this.router.navigate(['/explorar'], { queryParams: { categoria: filter } });
  }

  spaceLink(space: Space): string[] { return spacePublicPathCommands(space); }

  spaceLocationLabel(space: Space): string { return resolveSpaceLocationLabel(space); }

  spaceCategoryLabel(space: Space): string {
    return SPACE_CATEGORY_LABELS[getSpaceCategories(space)[0]];
  }

  hourlyRate(space: Space): number { return minHourlyRate(space); }

  coverThumb(space: Space): string { return spaceCoverThumb(space); }
}
